import React, { useEffect, useRef, useState } from "react";
import { ProtocolHalf, useTicketAd } from "../components/HalfA4Ticket";
import { ProtocolSlip } from "../components/CardSlips";
import PrintSizeSelect, { usePrintSize, usePrintColors } from "../components/PrintSizeSelect";
import DirectPrintBanner from "../components/DirectPrintBanner";
import QrScanButton from "../components/QrScanButton";
import PaymentModePicker, { toPayMode } from "../components/PaymentModePicker";
import { useSearchParams } from "react-router-dom";
import api, { fmtErr } from "../lib/api";
import { toJpeg } from "html-to-image";
import { printHtml } from "../lib/print";
import StatusBadge from "../components/StatusBadge";
import TicketCard from "../components/TicketCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Camera, Printer, Search, TicketCheck, Upload, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function TicketIssue() {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("txn") || "");
  const [txn, setTxn] = useState(null);
  const [templeName, setTempleName] = useState("");
  const [temple, setTemple] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [payMode, setPayMode] = useState("CASH");
  useEffect(() => { setPayMode(toPayMode(txn?.payment_details)); }, [txn?.transaction_id]); // eslint-disable-line
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const ticketRef = useRef(null);
  const printSize = usePrintSize();
  const portrait = printSize === "4x6";
  const lightPrint = usePrintColors() === "light";
  const halfTicket = ["half", "a5", "s21x15"].includes(printSize); // half-A4 ticket design
  const ticketAd = useTicketAd(temple?.temple_id, halfTicket);
  const searchRef = useRef(null);

  const search = async (q) => {
    const query = (q ?? code).trim();
    if (!query) return;
    setLoading(true);
    try {
      const r = await api.get(`/protocol/${encodeURIComponent(query)}`);
      setTxn(r.data);
      setPhoto(r.data.photo || null);
      if (r.data.temple_id) {
        const t = await api.get("/temples");
        const tm = t.data.find((x) => x.temple_id === r.data.temple_id) || null;
        setTemple(tm);
        setTempleName(tm?.name || "");
      }
    } catch (e) {
      setTxn(null);
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (params.get("txn")) search(params.get("txn")); }, []); // eslint-disable-line

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640 } });
      streamRef.current = stream;
      setCameraOn(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream; }, 100);
    } catch {
      toast.error("Camera not available. Use photo upload instead.");
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOn(false);
  };
  useEffect(() => () => stopCamera(), []);

  const capture = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = 480;
    canvas.height = Math.round((video.videoHeight / video.videoWidth) * 480) || 560;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    setPhoto(canvas.toDataURL("image/jpeg", 0.7));
    stopCamera();
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const savePhoto = async () => {
    if (!photo) { toast.error("Capture or upload a photo first"); return; }
    setLoading(true);
    try {
      await api.post(`/protocol/${txn.transaction_id}/photo`, { photo });
      toast.success("Photo saved");
      search();
    } catch (e) { toast.error(fmtErr(e)); } finally { setLoading(false); }
  };

  const issue = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/protocol/${txn.transaction_id}/issue`, { ...(photo ? { photo } : {}), payment_mode: payMode });
      setTxn(r.data);
      setPhoto(r.data.photo || null);
      toast.success(`Ticket ${r.data.ticket.ticket_no} issued — printing…`);
      // automatic print to default printer (silent when browser runs with kiosk-printing), then WhatsApp card, then back to scan
      setTimeout(async () => {
        try {
          const p = await api.post(`/tickets/${r.data.transaction_id}/print`);
          if (p.data.allowed && ticketRef.current) printHtml(ticketRef.current.outerHTML, "ticket-page", `Protocol_Ticket_${r.data.ticket?.ticket_no || r.data.transaction_id}`);
        } catch { /* manual print / reprint flow still available */ }
        try {
          if (ticketRef.current) {
            // WhatsApp gets the devotee copy only (not the office copy / advertisement of the half-A4 layout).
            const card = ticketRef.current.querySelector("[data-wa-card]") || ticketRef.current;
            const jpg = await toJpeg(card, { quality: 0.85, backgroundColor: "#ffffff", pixelRatio: 2 });
            const w = await api.post(`/protocol/${r.data.transaction_id}/whatsapp-card`, { image: jpg });
            toast.success(`Access card WhatsApp: ${w.data.status}`);
          }
        } catch (e) { toast.error(fmtErr(e)); }
        setTxn(null);
        setCode("");
        setPhoto(null);
        setTimeout(() => searchRef.current?.focus(), 150);
      }, 600);
    } catch (e) { toast.error(fmtErr(e)); } finally { setLoading(false); }
  };

  const [reprintReason, setReprintReason] = useState("");

  const handlePrint = async () => {
    try {
      const r = await api.post(`/tickets/${txn.transaction_id}/print`);
      if (r.data.allowed) {
        if (ticketRef.current) printHtml(ticketRef.current.outerHTML, "ticket-page", `Protocol_Ticket_${txn.ticket?.ticket_no || txn.transaction_id}`);
        setTxn(null);
        setPhoto(null);
        setCode("");
        toast.success("Ticket printed. Ready for next devotee.");
      }
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error("Ticket already printed. Reprint requires Supervisor approval.");
        search();
      } else {
        toast.error(fmtErr(e));
      }
    }
  };

  const submitReprint = async () => {
    if (!reprintReason.trim()) { toast.error("Reason for reprint is required"); return; }
    try {
      await api.post(`/tickets/${txn.transaction_id}/reprint`, { reason: reprintReason });
      toast.success("Reprint request sent for Supervisor approval");
      setReprintReason("");
      search();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const canCapture = txn && ["APPROVED", "PHOTO_CAPTURED"].includes(txn.status);
  const reqStatus = txn?.ticket?.reprint_request?.status;
  const alreadyPrinted = (txn?.ticket?.print_count || 0) >= 1;

  return (
    <div data-testid="ticket-issue-page">
      <DirectPrintBanner />
      <div className="flex justify-end mb-3"><PrintSizeSelect /></div>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900 mb-1">Protocol Office — Ticket Issue</h1>
      <p className="text-sm text-slate-500 mb-5">Search by Transaction ID or scan QR, verify details, capture photo and issue the access card.</p>

      <div className="scan-row flex gap-2 max-w-xl mb-6">
        <Input ref={searchRef} autoFocus data-testid="issue-search-input" value={code} onChange={(e) => setCode(e.target.value)}
          placeholder="Enter Transaction ID (e.g. PROT20260615000001)" onKeyDown={(e) => e.key === "Enter" && search()} />
        <QrScanButton onScan={(t) => { const id = t.startsWith("DARSHAN:") ? t.split(":").pop() : t; setCode(id); search(id); }} label="Scan" className="h-10" />
        <Button data-testid="issue-search-btn" onClick={() => search()} disabled={loading} className="bg-maroon-700 hover:bg-maroon-800 text-white">
          <Search className="w-4 h-4 mr-1" /> Search
        </Button>
      </div>

      {txn && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          <Card className="card-gold-top shadow-sm" data-testid="issue-details-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg text-maroon-900">Transaction Details</CardTitle>
              <StatusBadge status={txn.status} />
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div><p className="text-xs text-gold-700 font-bold uppercase">Transaction ID</p><p className="font-mono" data-testid="issue-txn-id">{txn.transaction_id}</p></div>
              <div><p className="text-xs text-gold-700 font-bold uppercase">Temple</p><p>{templeName}</p></div>
              <div><p className="text-xs text-gold-700 font-bold uppercase">Devotee Name</p><p data-testid="issue-devotee">{txn.devotee_name}</p></div>
              <div><p className="text-xs text-gold-700 font-bold uppercase">Reference</p><p>{txn.reference_name || "-"}</p></div>
              <div><p className="text-xs text-gold-700 font-bold uppercase">Mobile</p><p>{txn.mobile}</p></div>
              <div><p className="text-xs text-gold-700 font-bold uppercase">Devotees</p><p>{txn.num_devotees}</p></div>
              <div><p className="text-xs text-gold-700 font-bold uppercase">Darshan Type</p><p>{txn.darshan_type_name}</p></div>
              <div><p className="text-xs text-gold-700 font-bold uppercase">Paid / Free</p><p>{txn.paid_free} {txn.amount > 0 ? `· ₹${txn.amount}` : ""}</p></div>
              {txn.approved_by && <div><p className="text-xs text-gold-700 font-bold uppercase">Approved By</p><p>{txn.approved_by}</p></div>}
              {txn.letterhead_name && <div><p className="text-xs text-gold-700 font-bold uppercase">Letterhead</p><p>{txn.letterhead_name}</p></div>}
            </CardContent>
          </Card>

          <Card className="card-gold-top shadow-sm" data-testid="issue-photo-card">
            <CardHeader><CardTitle className="font-heading text-lg text-maroon-900">Devotee Photograph</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {!canCapture && txn.status !== "ISSUED" && txn.status !== "DARSHAN_COMPLETED" && (
                <p className="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded p-3" data-testid="issue-not-approved">This transaction is not approved. Photo capture is disabled.</p>
              )}
              {cameraOn ? (
                <div className="space-y-2">
                  <div className="relative rounded-md overflow-hidden border-2 border-gold-400 max-w-sm">
                    <video ref={videoRef} autoPlay playsInline className="w-full" data-testid="issue-camera-feed" />
                    <div className="absolute inset-0 border-[40px] border-black/30 pointer-events-none" />
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid="issue-photo-capture-btn" onClick={capture} className="bg-saffron-500 hover:bg-saffron-600 text-white"><Camera className="w-4 h-4 mr-1" /> Capture Photo</Button>
                    <Button variant="outline" onClick={stopCamera}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {photo ? (
                    <img src={photo} alt="devotee" className="w-40 h-48 object-cover rounded-md border-2 border-gold-400" data-testid="issue-photo-preview" />
                  ) : (
                    <div className="w-40 h-48 rounded-md border-2 border-dashed border-gold-400 flex items-center justify-center text-xs text-slate-400" data-testid="issue-photo-empty">No photo</div>
                  )}
                  {canCapture && (
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" onClick={startCamera} data-testid="issue-camera-start-btn"><Camera className="w-4 h-4 mr-1" /> {photo ? "Retake" : "Open Camera"}</Button>
                      <label>
                        <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
                        <span className="inline-flex items-center gap-1 px-3 py-2 text-sm border rounded-md cursor-pointer hover:bg-gold-50" data-testid="issue-photo-upload-btn"><Upload className="w-4 h-4" /> Upload Photo</span>
                      </label>
                      <Button onClick={savePhoto} disabled={!photo || loading} data-testid="issue-photo-save-btn" className="bg-maroon-700 hover:bg-maroon-800 text-white">Confirm Photo</Button>
                    </div>
                  )}
                </div>
              )}
              {canCapture && txn.amount > 0 && <PaymentModePicker value={payMode} onChange={setPayMode} testid="issue-pay-mode" />}
              {canCapture && (
                <Button onClick={issue} disabled={loading || !photo} data-testid="issue-generate-ticket-btn"
                  className="w-full h-12 bg-green-700 hover:bg-green-800 text-white font-heading tracking-widest">
                  <TicketCheck className="w-5 h-5 mr-2" /> GENERATE TICKET
                </Button>
              )}
              {canCapture && !photo && (
                <p className="text-xs text-slate-500" data-testid="issue-photo-required-hint">Photograph is required — the photo above is saved automatically when the ticket is generated.</p>
              )}
            </CardContent>
          </Card>

          {txn.ticket && (
            <div className="xl:col-span-2 space-y-3" data-testid="issue-ticket-section">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-heading text-lg font-bold text-maroon-900">Protocol Darshan Access Card</h2>
                <Button onClick={handlePrint} data-testid="issue-print-pass-btn" className="bg-saffron-500 hover:bg-saffron-600 text-white">
                  <Printer className="w-4 h-4 mr-1" /> {alreadyPrinted ? `Re-Print (printed ${txn.ticket.print_count}×)` : "Print (6 × 4 in)"}
                </Button>
              </div>
              {alreadyPrinted && reqStatus !== "APPROVED" && (
                <div className="rounded-md border border-orange-300 bg-orange-50 p-3 text-sm" data-testid="reprint-warning">
                  <p className="font-semibold text-orange-800">Ticket Already Printed ({txn.ticket.ticket_no})</p>
                  {reqStatus === "PENDING" ? (
                    <p className="text-orange-700 mt-1" data-testid="reprint-pending-note">Reprint request is pending Supervisor approval.</p>
                  ) : (
                    <div className="mt-2 flex gap-2 items-start">
                      <Textarea rows={1} placeholder="Reason for reprint (goes to Supervisor)" value={reprintReason}
                        onChange={(e) => setReprintReason(e.target.value)} data-testid="reprint-reason-input" className="bg-white" />
                      <Button variant="outline" onClick={submitReprint} data-testid="reprint-request-btn"
                        className="border-orange-400 text-orange-800 hover:bg-orange-100 shrink-0">Request Reprint</Button>
                    </div>
                  )}
                  {reqStatus === "REJECTED" && (
                    <p className="text-red-700 mt-1 text-xs" data-testid="reprint-rejected-note">Last request rejected: {txn.ticket.reprint_request.remarks || "no remarks"}</p>
                  )}
                </div>
              )}
              {alreadyPrinted && reqStatus === "APPROVED" && (
                <p className="rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-800 font-semibold" data-testid="reprint-approved-note">
                  Reprint approved by {txn.ticket.reprint_request.approved_by || txn.ticket.reprint_request.approved_by_name || "Supervisor"} — you may print now.
                </p>
              )}
              <div className={`overflow-x-auto ${lightPrint ? "print-light" : ""}`} ref={ticketRef}>{halfTicket ? (
                <div className="flex flex-col gap-4">
                  <ProtocolHalf txn={txn} temple={temple} templeName={templeName} copyLabel="Devotee Copy" ad={ticketAd} waCard />
                  <ProtocolHalf txn={txn} temple={temple} templeName={templeName} copyLabel="Office Copy" ad={ticketAd} />
                </div>
              ) : printSize === "slip" ? <ProtocolSlip txn={txn} temple={temple} /> : <TicketCard txn={txn} temple={temple} templeName={templeName} portrait={portrait} />}</div>
            </div>
          )}
        </div>
      )}
      {!txn && !loading && (
        <div className="text-center py-16 text-slate-400" data-testid="issue-empty-state">
          <RefreshCw className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm">Enter a Transaction ID to begin ticket issue.</p>
        </div>
      )}
    </div>
  );
}
